import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Aviso from '../componentes/Aviso';
import Carregando from '../componentes/Carregando';
import Segmentado from '../componentes/Segmentado';
import { contasEmAberto } from '../lib/consultas';
import {
  GEOMETRIA_CELULAR, MAXIMO_DE_FOTOS, nomeDoArquivo, paginar, PERIODOS, prepararLista,
  type PeriodoDaLista,
} from '../lib/listaDeContas';
import { folhaEmPng, folhasEmPdf, fotoParaArquivo, pdfParaArquivo, type Tela } from '../lib/folhaSaida';
import {
  baixarVarios, compartilharArquivos, estaInstalado, podeCompartilhar, type ResultadoDoEnvio,
} from '../lib/compartilhar';
import { brl, hojeISO } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

// =============================================================================
// MANDAR A LISTA (22/09/2026) — pedido do dono: "enviar para o meu pai um
// arquivo em .pdf ou em imagem que mostre para ele todas as contas para os
// próximos dias".
//
// O painel mostra AS PRÓPRIAS PÁGINAS que vão sair — o que o dono vê é o que o
// pai recebe — e manda pelo Compartilhar do celular (WhatsApp → o pai).
//
// O arquivo fica PRONTO antes do toque: o iPhone só aceita abrir o
// Compartilhar dentro do gesto, e uma ida à rede no meio o faz recusar. Por
// isso a busca acontece ao ABRIR o painel (sempre fresca: alguém pode ter
// pago uma conta agora há pouco), e o toque só chama share().
// =============================================================================

const CHAVE_PERIODO = 'serginho:lista-de-contas:periodo';

function periodoGuardado(): PeriodoDaLista {
  try {
    const v = localStorage.getItem(CHAVE_PERIODO);
    const achado = PERIODOS.find((p) => String(p.id) === v);
    if (achado) return achado.id;
  } catch { /* navegação privada: fica o padrão */ }
  return 15;
}

function guardarPeriodo(p: PeriodoDaLista) {
  try { localStorage.setItem(CHAVE_PERIODO, String(p)); } catch { /* sem armazenamento: tudo bem */ }
}

const criarTela = () => document.createElement('canvas') as unknown as Tela;

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

export function EnvioDaLista({ aoFechar, buscar = contasEmAberto }: {
  aoFechar: () => void;
  /** De onde vêm as contas — trocável só para mostrar o painel com dados de
   *  exemplo; a tela usa a consulta de verdade. */
  buscar?: typeof contasEmAberto;
}) {
  // gcTime 0 + refetch ao montar: cada abertura do painel busca de novo. O
  // `recarregar()` da tela invalida ['contas-pagar'] por prefixo — pagar uma
  // conta com o painel aberto atualiza a lista junto.
  const consulta = useQuery({
    queryKey: ['contas-pagar', 'envio'], queryFn: buscar,
    staleTime: 0, gcTime: 0, refetchOnMount: 'always',
  });
  const [periodo, setPeriodo] = useState<PeriodoDaLista>(periodoGuardado);
  const [comSalarios, setComSalarios] = useState(true);
  const [comCodigo, setComCodigo] = useState(false);
  const [resultado, setResultado] = useState<ResultadoDoEnvio | null>(null);
  const [compartilhando, setCompartilhando] = useState(false);
  const [baixando, setBaixando] = useState(false);

  const hoje = hojeISO();
  const contas = consulta.data;
  const lista = useMemo(
    () => (contas ? prepararLista(contas, { hoje, periodo, comSalarios }) : null),
    [contas, hoje, periodo, comSalarios],
  );
  // "Lista feita em …": a hora em que os DADOS chegaram, não a do toque.
  const feitaEm = consulta.dataUpdatedAt;
  const folhas = useMemo(
    () => (lista ? paginar(lista, GEOMETRIA_CELULAR, { agora: new Date(feitaEm), comCodigo }) : []),
    [lista, feitaEm, comCodigo],
  );
  const pdf = useMemo(
    () => (lista && folhas.length ? pdfParaArquivo(folhasEmPdf(folhas), nomeDoArquivo(lista, 'pdf')) : null),
    [lista, folhas],
  );
  const fotosLigadas = folhas.length > 0 && folhas.length <= MAXIMO_DE_FOTOS;

  // As fotos são desenhadas FORA do render (canvas de 1024 × 1280 por página)
  // e guardadas junto com as folhas de onde vieram: resposta atrasada de um
  // período antigo nunca aparece no lugar do período novo.
  const [fotos, setFotos] = useState<{ de: typeof folhas; previas: string[]; arquivos: File[] } | null>(null);
  const [erroFotos, setErroFotos] = useState<string | null>(null);
  useEffect(() => {
    if (!lista || !folhas.length) return;
    let vivo = true;
    setErroFotos(null);
    // Um tique antes: deixa a tela pintar "Preparando…" primeiro.
    const id = setTimeout(() => {
      try {
        const aDesenhar = fotosLigadas ? folhas : folhas.slice(0, 1);
        const previas = aDesenhar.map((f) => folhaEmPng(f, criarTela));
        const arquivos = fotosLigadas
          ? previas.map((p, i) => fotoParaArquivo(p, nomeDoArquivo(lista, 'png', { numero: i + 1, total: folhas.length })))
          : [];
        if (vivo) setFotos({ de: folhas, previas, arquivos });
      } catch (e) {
        if (vivo) setErroFotos(e instanceof Error ? e.message : 'Não deu para desenhar as fotos. Envie em PDF.');
      }
    }, 30);
    return () => { vivo = false; clearTimeout(id); };
  }, [lista, folhas, fotosLigadas]);
  const fotosProntas = fotos && fotos.de === folhas ? fotos : null;

  const instalado = estaInstalado();
  const buscando = consulta.isFetching;
  // Busca de novo que FALHOU deixa a lista velha na tela: não se envia lista
  // que pode ter conta já paga (revisão de 22/09/2026).
  const falhou = consulta.isError;
  const bloqueado = buscando || falhou || compartilhando || baixando || !lista;

  const enviar = (arquivos: File[]) => {
    // share() primeiro, antes de qualquer outra coisa (a ativação do toque).
    const promessa = compartilharArquivos(arquivos);
    setResultado(null);
    setCompartilhando(true);
    void promessa.then((r) => {
      setResultado(r === 'cancelado' ? null : r);
      setCompartilhando(false);
    });
  };

  const baixar = (arquivos: File[]) => {
    setResultado(null);
    setBaixando(true);
    // Um a um e espaçados: no mesmo instante, o navegador bloqueia do 2º em
    // diante. No iPhone o arquivo cai no app Arquivos, em Transferências.
    void baixarVarios(arquivos).finally(() => setBaixando(false));
  };

  const arquivosDasFotos = fotosProntas?.arquivos ?? [];
  const compartilhaPdf = pdf ? podeCompartilhar([pdf]) : false;
  const compartilhaFotos = arquivosDasFotos.length > 0 && podeCompartilhar(arquivosDasFotos);

  return (
    <div className="cartao space-y-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="rotulo">Mandar a lista das contas</h2>
        <button type="button" className="text-xs text-dim underline" onClick={aoFechar}>fechar</button>
      </div>
      <p className="text-xs text-mute">
        Sai uma lista para quem não usa o sistema: letra grande, o total em destaque, as vencidas no
        topo e cada dia com as suas contas. Vão TODAS as contas em aberto do período — os filtros da
        tela não contam aqui.
      </p>

      <div>
        <p className="rotulo">Até quando</p>
        <Segmentado
          itens={PERIODOS.map((p) => ({ id: String(p.id), rotulo: p.rotulo }))}
          valor={String(periodo)}
          aoMudar={(id) => {
            const p = PERIODOS.find((x) => String(x.id) === id)?.id ?? 15;
            setPeriodo(p);
            guardarPeriodo(p);
            setResultado(null);
          }}
        />
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
        <label className="flex items-center gap-2">
          <input id="env-salarios" type="checkbox" checked={comSalarios}
                 onChange={(e) => { setComSalarios(e.target.checked); setResultado(null); }} />
          Incluir salários e pró-labore
        </label>
        <label className="flex items-center gap-2">
          <input id="env-codigo" type="checkbox" checked={comCodigo}
                 onChange={(e) => { setComCodigo(e.target.checked); setResultado(null); }} />
          Incluir os códigos de barras
        </label>
      </div>

      {consulta.isPending ? <Carregando /> : null}
      {consulta.error ? (
        <Aviso tipo="erro">
          {mensagemDeErro(consulta.error)}
          {consulta.data ? ' — a lista abaixo pode estar velha e não vai ser enviada. Feche e abra de novo.' : ''}
        </Aviso>
      ) : null}

      {lista && (
        <>
          <div className="rounded-[0.625rem] border border-edge bg-panel-alt p-3 text-sm">
            <p>
              <strong className="numero font-medium">{brl(lista.total)}</strong>
              {' '}em {plural(lista.quantidade, 'conta', 'contas')}
            </p>
            <p className="text-xs text-dim">
              {lista.atrasadas.length > 0 && <>{plural(lista.atrasadas.length, 'vencida', 'vencidas')} · </>}
              {lista.semValor > 0 && <>{plural(lista.semValor, 'sem valor', 'sem valor')} · </>}
              {fotosLigadas
                ? `vai em ${plural(folhas.length, 'foto', 'fotos')} ou num PDF`
                : `${folhas.length} páginas: vai só em PDF (mais de ${MAXIMO_DE_FOTOS} fotos cansa quem lê)`}
            </p>
          </div>

          {lista.atrasadas.length > 0 && (
            <Aviso tipo="alerta">
              {plural(lista.atrasadas.length, 'conta vencida continua', 'contas vencidas continuam')} em aberto.
              Se alguma já foi paga, dê baixa antes de enviar — senão ele vai ler como atrasada.
            </Aviso>
          )}
          {lista.semValor > 0 && (
            <Aviso tipo="info">
              {plural(lista.semValor, 'conta está', 'contas estão')} sem valor e sai como "a definir".
              Se a fatura já chegou, ponha o valor antes.
            </Aviso>
          )}

          <div className="grid gap-2 sm:grid-cols-2">
            {/* Com erro no desenho, o botão das fotos some (senão ficaria em
                "Preparando…" para sempre) e sobra o PDF, como o aviso diz. */}
            {fotosLigadas && !erroFotos && (compartilhaFotos || !fotosProntas) && (
              <button type="button" className="botao-primario w-full py-2 text-sm"
                      disabled={bloqueado || !fotosProntas}
                      onClick={() => enviar(arquivosDasFotos)}>
                {!fotosProntas ? 'Preparando as fotos…' : `Enviar ${plural(folhas.length, 'foto', 'fotos')}`}
              </button>
            )}
            {pdf && compartilhaPdf && (
              <button type="button" className="botao-secundario w-full py-2 text-sm"
                      disabled={bloqueado} onClick={() => enviar([pdf])}>
                Enviar PDF
              </button>
            )}
          </div>
          {/* BAIXAR fica sempre à mão (pedido do dono, 22/09/2026): guarda o
              arquivo no aparelho — PC, Android ou iPhone, inclusive com o
              sistema aberto pelo ícone. O que prende o iPhone é NAVEGAR para
              o arquivo, não baixar com o atributo `download`. */}
          <div className="grid gap-2 sm:grid-cols-2">
            {pdf && (
              <button type="button" className="botao-secundario w-full py-2 text-sm"
                      disabled={bloqueado} onClick={() => baixar([pdf])}>
                {baixando ? 'Baixando…' : 'Baixar o PDF'}
              </button>
            )}
            {fotosLigadas && fotosProntas && !erroFotos && (
              <button type="button" className="botao-secundario w-full py-2 text-sm"
                      disabled={bloqueado} onClick={() => baixar(arquivosDasFotos)}>
                {baixando ? 'Baixando…' : `Baixar ${plural(arquivosDasFotos.length, 'foto', 'fotos')}`}
              </button>
            )}
          </div>
          <p className="text-xs text-dim">
            {instalado
              ? 'O que você baixa fica no aparelho, no app Arquivos (pasta Transferências).'
              : 'O que você baixa fica na pasta de downloads do aparelho.'}
            {arquivosDasFotos.length > 1 && ' Baixando as fotos, o navegador pode pedir permissão para vários arquivos.'}
          </p>
          {pdf && !compartilhaPdf && (
            <Aviso tipo="info">
              Este aparelho não abriu o Compartilhar: baixe o arquivo e anexe no WhatsApp,
              ou segure o dedo numa página abaixo e toque em "Compartilhar".
            </Aviso>
          )}
          {buscando && !consulta.isPending && <p className="text-xs text-dim">Buscando as contas de novo…</p>}
          {resultado === 'enviado' && <Aviso tipo="ok">Pronto: a lista foi para o app que você escolheu.</Aviso>}
          {resultado === 'toque-de-novo' && (
            <Aviso tipo="alerta">O celular pediu um toque novo: toque em Enviar de novo.</Aviso>
          )}
          {resultado === 'falhou' && (
            <Aviso tipo="erro">
              O Compartilhar não abriu. Tente o PDF; se não der, abra o sistema pelo Safari e envie de lá.
            </Aviso>
          )}
          {erroFotos && <Aviso tipo="alerta">{erroFotos}</Aviso>}
          {fotosLigadas && (
            <p className="text-xs text-dim">
              No WhatsApp, deixe as fotos em HD antes de enviar: a letra chega mais nítida.
            </p>
          )}

          <div className="space-y-2">
            <p className="rotulo">{fotosLigadas ? 'Como ele vai ver' : 'A primeira página'}</p>
            {fotosProntas
              ? fotosProntas.previas.map((src, i) => (
                  <img key={i} src={src} alt={`Página ${i + 1} de ${folhas.length} da lista`}
                       className="w-full border border-edge" />
                ))
              : !erroFotos && <p className="text-xs text-dim">Desenhando as páginas…</p>}
          </div>
        </>
      )}
    </div>
  );
}
